import { Activity } from 'react';

import { useSearchParams } from 'react-router-dom';
import Popover from '@ui/popover/popover';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import Button from '@ui/buttons/button/button';
import { RegistrationFilter } from '@/types/all-attendees.types';

type FilterOption = {
  label: string;
  value: RegistrationFilter;
};

const OPTIONS: FilterOption[] = [
  { label: 'All', value: 'all' },
  { label: 'Registered', value: 'registered' },
  { label: 'Invited', value: 'invited' },
  { label: 'Added', value: 'added' },
];

type Props = {
  /** Controlled mode - pass both to keep the selection in local state instead of the URL (e.g. inside a modal) */
  value?: RegistrationFilter;
  onChange?: (value: RegistrationFilter) => void;
  id?: string;
};

// By default the selection lives in the registrationStatus search param (source of truth), so filtered
// views are shareable, refresh-safe, and reset naturally on navigation.
const ProfilesRegistrationStatusFilter = ({ value, onChange, id = 'profiles-registration-status-filter' }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramValue = value ?? searchParams.get('registrationStatus');
  const current: RegistrationFilter = OPTIONS.find((o) => o.value === paramValue)?.value ?? 'all';
  const currentLabel = OPTIONS.find((o) => o.value === current)?.label ?? 'All';

  const handleSelect = (value: RegistrationFilter) => {
    if (onChange) {
      onChange(value);
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === 'all') {
          next.delete('registrationStatus');
        } else {
          next.set('registrationStatus', value);
        }
        return next;
      },
      // filter changes refine the current view rather than navigate, so don't stack history entries
      { replace: true }
    );
  };

  return (
    <Popover
      id={id}
      preferredPosition="bottom-left"
      width="160px"
      cycleWithArrowKeys
      refocusTriggerOnClose
      renderTriggerButton={({ open, closing }) => (
        <Button
          clear
          padding="0 16px"
          borderRadius="100px"
          className="flex ai-center gap-8"
          designSize="small"
          border={`1px solid var(--global-ico-text-${open ? 1 : 3})`}
          hoverBorder="1px solid var(--global-ico-text-1)"
        >
          <Text className="P2" whiteSpace="nowrap" hoverColor="var(--global-ico-text-3)" design="GlobalIcoText3">
            Registration Status:
          </Text>
          <Text className="P2" whiteSpace="nowrap" hoverColor="var(--global-ico-text-1)" design="GlobalIcoText1">
            {currentLabel}
          </Text>
          <Text
            inheritButtonHover
            color={`var(--global-ico-text-${open ? 1 : 3})`}
            hoverColor="var(--global-ico-text-1)"
          >
            <RotatableIcon name={ICONS.KEYBOARD_ARROW_DOWN} size={12} rotate={open && !closing ? '180deg' : '0'} />
          </Text>
        </Button>
      )}
    >
      {({ handleClose }) => (
        <Container className="flex fd-column">
          {OPTIONS.map((option) => (
            <Button
              key={option.value}
              style={{ cursor: 'pointer' }}
              clear
              className="flex ai-center padding-h-16 padding-v-8"
              hoverBackgroundColor="var(--global-cont-3)"
              onClick={() => {
                handleSelect(option.value);
                handleClose();
              }}
            >
              <Container className="flex ai-center jc-space-between" width="100%">
                <Text className="P2" design="GlobalIcoText1">
                  {option.label}
                </Text>
                <Activity mode={current === option.value ? 'visible' : 'hidden'}>
                  <Icon name={ICONS.CHECK} size={12} color="var(--global-ico-text-1)" />
                </Activity>
              </Container>
            </Button>
          ))}
        </Container>
      )}
    </Popover>
  );
};

export default ProfilesRegistrationStatusFilter;
